import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { CountUp } from "@/components/animations";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    Cell,
    ResponsiveContainer,
} from "recharts";
import {
    Users,
    TrendingUp,
    Shield,
    Activity,
    Settings,
    AlertCircle,
    Github,
    Globe,
    Search,
    RefreshCw,
    Clock3,
    Database,
    CheckCircle2,
    ChevronRight,
    Calendar,
    ChevronLeft,
} from "lucide-react";
import { EASING, DURATION } from "@/lib/motionSystem";
import { TIER_COLORS, TIER_ORDER } from "@/lib/tiers";

const PAGE_SIZE = 20;

function formatDate(value) {
    if (!value) return "—";
    try {
        return new Date(value).toLocaleDateString("en-IN", { year: "numeric", month: "short", day: "numeric" });
    } catch {
        return "—";
    }
}

function safeNum(value) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
}

function StatCard({ label, value, icon: Icon, color, suffix = "" }) {
    return (
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex items-center justify-between mb-1.5">
                <Icon size={16} style={{ color }} />
                <span className="font-mono text-2xl font-bold text-slate-900">
                    <CountUp target={safeNum(value)} duration={0.9} />
                    {suffix}
                </span>
            </div>
            <p className="text-slate-500 text-xs">{label}</p>
        </div>
    );
}

function Pagination({ page, totalPages, onPage }) {
    if (!totalPages || totalPages <= 1) return null;
    return (
        <div className="flex items-center gap-2">
            <button
                onClick={() => onPage(Math.max(1, page - 1))}
                disabled={page <= 1}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
                <ChevronLeft size={12} /> Prev
            </button>
            <span className="text-xs text-slate-500 font-mono">
                {page} / {totalPages}
            </span>
            <button
                onClick={() => onPage(Math.min(totalPages, page + 1))}
                disabled={page >= totalPages}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
                Next <ChevronRight size={12} />
            </button>
        </div>
    );
}

export default function AdminConsole() {
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [usersPage, setUsersPage] = useState({ users: [], page: 1, total: 0, total_pages: 1 });
    const [scoresPage, setScoresPage] = useState({ scores: [], page: 1, total: 0, total_pages: 1 });
    const [auditPage, setAuditPage] = useState({ entries: [], page: 1, total: 0, total_pages: 1 });
    const [auditPageNum, setAuditPageNum] = useState(1);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [backfillLoading, setBackfillLoading] = useState(false);
    const [backfillResult, setBackfillResult] = useState(null);

    // Filters
    const [includeDemo, setIncludeDemo] = useState(false);
    const [userQuery, setUserQuery] = useState("");
    const [roleFilter, setRoleFilter] = useState("");
    const [tierFilter, setTierFilter] = useState("");
    const [sortBy, setSortBy] = useState("created_desc");
    const [usersPageNum, setUsersPageNum] = useState(1);

    const [scoreQuery, setScoreQuery] = useState("");
    const [scoreTierFilter, setScoreTierFilter] = useState("");
    const [scoresPageNum, setScoresPageNum] = useState(1);

    const fetchStats = async () => {
        const { data } = await api.get("/api/admin/stats", { params: { include_demo: includeDemo } });
        setStats(data);
    };

    const fetchUsers = async () => {
        const { data } = await api.get("/api/admin/users", {
            params: {
                q: userQuery,
                role: roleFilter,
                tier: tierFilter,
                sort: sortBy,
                page: usersPageNum,
                page_size: PAGE_SIZE,
                include_demo: includeDemo,
            },
        });
        setUsersPage(data);
    };

    const fetchScores = async () => {
        const { data } = await api.get("/api/admin/scores", {
            params: {
                q: scoreQuery,
                tier: scoreTierFilter,
                page: scoresPageNum,
                page_size: PAGE_SIZE,
                include_demo: includeDemo,
            },
        });
        setScoresPage(data);
    };

    const fetchAudit = async () => {
        const { data } = await api.get("/api/admin/consent-audit", {
            params: { page: auditPageNum, page_size: PAGE_SIZE }
        });
        setAuditPage(data);
    };

    const fetchAll = async () => {
        setLoading(true);
        setError(null);
        try {
            await Promise.all([fetchStats(), fetchUsers(), fetchScores(), fetchAudit()]);
        } catch (err) {
            setError(err.response?.data?.detail || "Failed to load admin data");
        }
        setLoading(false);
    };

    const runSnapshotBackfill = async () => {
        if (!window.confirm("Are you sure you want to run the snapshot backfill? This will update all legacy score records and cannot be undone.")) {
            return;
        }
        setBackfillLoading(true);
        setBackfillResult(null);
        try {
            const { data } = await api.post("/api/admin/backfill-snapshots");
            setBackfillResult(`Backfilled ${data.backfilled || 0} of ${data.total_candidates || 0} candidate scores.`);
            await Promise.all([fetchStats(), fetchScores()]);
        } catch (err) {
            setBackfillResult(err.response?.data?.detail || "Backfill failed. Check backend logs.");
        } finally {
            setBackfillLoading(false);
        }
    };

    // Initial + dependency-driven loads. Keep stats and audit on the simpler path.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { fetchAll(); }, []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { if (!loading) { fetchUsers(); fetchStats(); } }, [usersPageNum, userQuery, roleFilter, tierFilter, sortBy, includeDemo]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { if (!loading) fetchScores(); }, [scoresPageNum, scoreQuery, scoreTierFilter, includeDemo]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { if (!loading) fetchAudit(); }, [auditPageNum]);

    const tierData = useMemo(
        () =>
            Object.entries(stats?.tier_distribution || {}).map(([tier, count]) => ({
                name: tier,
                count,
                fill: TIER_COLORS[tier] || "#64748b",
            })),
        [stats?.tier_distribution],
    );

    const connectors = [
        { name: "GitHub OAuth", status: "active", icon: Github },
        { name: "Google OAuth", status: "test", icon: Globe },
        { name: "Twitter/X", status: "planned", icon: Globe },
        { name: "LinkedIn", status: "url_only", icon: Globe },
    ];

    const statusBadge = {
        active: { label: "Active", cls: "bg-emerald-50 text-emerald-700 border-emerald-100" },
        test: { label: "Test Mode", cls: "bg-amber-50 text-amber-700 border-amber-100" },
        planned: { label: "Planned", cls: "bg-slate-100 text-slate-600 border-slate-200" },
        url_only: { label: "URL Only", cls: "bg-cyan-50 text-cyan-700 border-cyan-100" },
    };

    const flags = [
        { name: "AI Explanations", value: "Claude + fallback", active: true },
        { name: "GitHub OAuth", value: "Enabled", active: true },
        { name: "PDF Export", value: "Enabled", active: true },
        { name: "Vibe AI Assistant", value: "Enabled", active: true },
        { name: "DigiLocker", value: "Coming Soon", active: false },
        { name: "Email Notifications", value: "Not Implemented", active: false },
    ];

    if (loading) {
        return (
            <div className="pt-24 flex justify-center min-h-dvh bg-transparent" data-testid="admin-console">
                <div className="text-slate-500">Loading admin operations...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="pt-24 px-6 flex justify-center min-h-dvh bg-transparent" data-testid="admin-console">
                <div className="rounded-xl border border-rose-200 bg-white p-8 text-center max-w-md shadow-sm">
                    <AlertCircle size={32} className="text-rose-500 mx-auto mb-3" />
                    <h2 className="font-heading text-xl font-semibold text-slate-900 mb-2">Access Denied</h2>
                    <p className="text-slate-500 text-sm">{error}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="pt-24 pb-16 px-6 md:px-12 lg:px-24 min-h-dvh bg-transparent" data-testid="admin-console">
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: DURATION.pageTransition, ease: EASING.standard }}
            >
                <div className="flex items-start justify-between gap-4 mb-8 flex-wrap">
                    <div>
                        <p className="text-xs uppercase tracking-[0.3em] text-slate-500 font-semibold mb-3">Admin Operations</p>
                        <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tighter text-slate-900 mb-2">Admin Console</h1>
                        <p className="text-slate-600 text-base">Drill into any user or score to see the full submission story.</p>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm text-slate-700 cursor-pointer hover:bg-slate-50 transition-all">
                            <input
                                type="checkbox"
                                checked={includeDemo}
                                onChange={(e) => {
                                    setUsersPageNum(1);
                                    setScoresPageNum(1);
                                    setIncludeDemo(e.target.checked);
                                }}
                                className="rounded"
                            />
                            Include demo users
                        </label>
                        <button
                            onClick={fetchAll}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-200 bg-white text-slate-700 text-sm font-medium hover:bg-slate-50 active:scale-[0.98] transition-all"
                        >
                            <RefreshCw size={14} /> Refresh
                        </button>
                        <button
                            onClick={runSnapshotBackfill}
                            disabled={backfillLoading}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-blue-200 bg-blue-50 text-blue-700 text-sm font-medium hover:bg-blue-100 active:scale-[0.98] transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                            <Database size={14} /> {backfillLoading ? "Backfilling..." : "Backfill Snapshots"}
                        </button>
                    </div>
                </div>
                {backfillResult && (
                    <div className="mb-6 rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 shadow-sm">
                        {backfillResult}
                    </div>
                )}
            </motion.div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4" data-testid="admin-stats">
                <StatCard label="Total Users" value={stats?.total_users} icon={Users} color="#2563eb" />
                <StatCard label="Scores Generated" value={stats?.total_scores} icon={TrendingUp} color="#16a34a" />
                <StatCard label="Average Score" value={Math.round(safeNum(stats?.avg_score))} icon={Activity} color="#0891b2" />
                <StatCard label="Avg Confidence" value={Math.round(safeNum(stats?.avg_confidence) * 100)} icon={Shield} color="#7c3aed" suffix="%" />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
                <StatCard label="Scores Today" value={stats?.scores_today} icon={Calendar} color="#ea580c" />
                <StatCard label="Scores This Week" value={stats?.scores_this_week} icon={TrendingUp} color="#9333ea" />
                <StatCard label="Active Scoring Users" value={stats?.unique_users_with_score} icon={Users} color="#0d9488" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                <section className="lg:col-span-2 rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
                    <h3 className="font-heading text-lg font-medium text-slate-900 mb-4">Score Distribution</h3>
                    {tierData.length > 0 ? (
                        <ResponsiveContainer width="100%" height={230}>
                            <BarChart data={tierData}>
                                <XAxis dataKey="name" tick={{ fill: "#64748b", fontSize: 12 }} axisLine={false} tickLine={false} />
                                <YAxis tick={{ fill: "#64748b", fontSize: 11, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} allowDecimals={false} />
                                <Tooltip contentStyle={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "10px" }} />
                                <Bar dataKey="count" radius={[8, 8, 0, 0]} barSize={44}>
                                    {tierData.map((entry, idx) => <Cell key={`tier-${entry.name}-${idx}`} fill={entry.fill} />)}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    ) : (
                        <p className="text-slate-500 text-sm">No score data yet.</p>
                    )}
                </section>

                <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
                    <h3 className="font-heading text-lg font-medium text-slate-900 mb-4">System Health</h3>
                    <div className="space-y-3">
                        <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-200">
                            <div className="flex items-center gap-2 text-sm text-slate-700">
                                <Clock3 size={14} className="text-slate-500" />
                                API Response Posture
                            </div>
                            <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-100 text-emerald-700">Stable</span>
                        </div>
                        <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-200">
                            <div className="flex items-center gap-2 text-sm text-slate-700">
                                <Database size={14} className="text-slate-500" />
                                Data Integrity
                            </div>
                            <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-100 text-emerald-700">Healthy</span>
                        </div>
                        <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-200">
                            <div className="flex items-center gap-2 text-sm text-slate-700">
                                <CheckCircle2 size={14} className="text-slate-500" />
                                Auth Flows
                            </div>
                            <span className="text-xs px-2 py-0.5 rounded-full bg-cyan-50 border border-cyan-100 text-cyan-700">Operational</span>
                        </div>
                    </div>
                </section>
            </div>

            <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm mb-8">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 mb-4">
                    <h3 className="font-heading text-lg font-medium text-slate-900">
                        Users <span className="text-sm font-normal text-slate-500">({usersPage.total} total)</span>
                    </h3>
                    <div className="flex flex-wrap gap-2">
                        <div className="relative">
                            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                            <input
                                value={userQuery}
                                onChange={(event) => {
                                    setUsersPageNum(1);
                                    setUserQuery(event.target.value);
                                }}
                                placeholder="Search user or email"
                                className="pl-8 pr-3 py-2 rounded-lg border border-slate-200 bg-white text-sm text-slate-700 focus:outline-none focus:border-slate-300"
                            />
                        </div>
                        <select
                            value={roleFilter}
                            onChange={(event) => {
                                setUsersPageNum(1);
                                setRoleFilter(event.target.value);
                            }}
                            className="px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm text-slate-700"
                        >
                            <option value="">All Roles</option>
                            <option value="admin">Admin</option>
                            <option value="user">User</option>
                        </select>
                        <select
                            value={tierFilter}
                            onChange={(event) => {
                                setUsersPageNum(1);
                                setTierFilter(event.target.value);
                            }}
                            className="px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm text-slate-700"
                        >
                            <option value="">All Tiers</option>
                            {TIER_ORDER.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                        <select
                            value={sortBy}
                            onChange={(event) => {
                                setUsersPageNum(1);
                                setSortBy(event.target.value);
                            }}
                            className="px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm text-slate-700"
                        >
                            <option value="created_desc">Newest</option>
                            <option value="created_asc">Oldest</option>
                            <option value="score_desc">Score High-Low</option>
                            <option value="score_asc">Score Low-High</option>
                            <option value="name_asc">Name A-Z</option>
                            <option value="name_desc">Name Z-A</option>
                        </select>
                    </div>
                </div>

                <div className="relative rounded-lg border border-slate-200">
                    <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-white to-transparent md:hidden z-10" />
                    <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="admin-user-table">
                        <thead className="bg-slate-100">
                            <tr className="text-left text-xs text-slate-600 uppercase border-b border-slate-200">
                                <th className="py-2.5 px-3">Name</th>
                                <th className="py-2.5 px-3">Email</th>
                                <th className="py-2.5 px-3">Role</th>
                                <th className="py-2.5 px-3">Score</th>
                                <th className="py-2.5 px-3">Tier</th>
                                <th className="py-2.5 px-3">Scores</th>
                                <th className="py-2.5 px-3">Joined</th>
                                <th className="py-2.5 px-3"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {usersPage.users.map((entry) => (
                                <tr
                                    key={entry.id}
                                    className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                                    onClick={() => navigate(`/genz-credit-scoring/admin/users/${entry.id}`)}
                                    data-testid={`admin-user-row-${entry.id}`}
                                >
                                    <td className="py-2.5 px-3 text-slate-900 font-medium">
                                        {entry.name || "—"}
                                        {entry.is_demo && (
                                            <span className="ml-2 text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">DEMO</span>
                                        )}
                                    </td>
                                    <td className="py-2.5 px-3 text-slate-600">{entry.email || "—"}</td>
                                    <td className="py-2.5 px-3">
                                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${entry.role === "admin" ? "bg-violet-100 text-violet-700" : "bg-slate-100 text-slate-600"}`}>
                                            {entry.role || "user"}
                                        </span>
                                    </td>
                                    <td className="py-2.5 px-3 font-mono text-slate-700">
                                        {entry.score ? <span style={{ color: TIER_COLORS[entry.tier] || "#334155" }}>{entry.score}</span> : "—"}
                                    </td>
                                    <td className="py-2.5 px-3 text-slate-600">{entry.tier || "—"}</td>
                                    <td className="py-2.5 px-3 font-mono text-slate-600 text-xs">{entry.total_scores ?? 0}</td>
                                    <td className="py-2.5 px-3 text-slate-500 text-xs">{formatDate(entry.created_at)}</td>
                                    <td className="py-2.5 px-3 text-slate-400">
                                        <ChevronRight size={14} />
                                    </td>
                                </tr>
                            ))}
                            {usersPage.users.length === 0 && (
                                <tr>
                                    <td colSpan={8} className="py-6 text-center text-slate-500 text-sm">No users match current filters.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                    </div>
                </div>
                <div className="flex items-center justify-between mt-4 flex-wrap gap-2">
                    <p className="text-xs text-slate-500">
                        Showing {usersPage.users.length} of {usersPage.total}
                    </p>
                    <Pagination page={usersPage.page} totalPages={usersPage.total_pages} onPage={setUsersPageNum} />
                </div>
            </section>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
                    <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
                        <h3 className="font-heading text-lg font-medium text-slate-900">
                            Recent Scores <span className="text-xs font-normal text-slate-500">({scoresPage.total})</span>
                        </h3>
                        <div className="flex items-center gap-2 flex-wrap">
                            <select
                                value={scoreTierFilter}
                                onChange={(event) => {
                                    setScoresPageNum(1);
                                    setScoreTierFilter(event.target.value);
                                }}
                                className="px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white text-xs text-slate-700"
                            >
                                <option value="">All Tiers</option>
                                {TIER_ORDER.map(t => <option key={t} value={t}>{t}</option>)}
                            </select>
                            <div className="relative">
                                <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                                <input
                                    value={scoreQuery}
                                    onChange={(event) => {
                                        setScoresPageNum(1);
                                        setScoreQuery(event.target.value);
                                    }}
                                    placeholder="Find user or email"
                                    className="pl-8 pr-3 py-1.5 rounded-lg border border-slate-200 bg-white text-sm text-slate-700 focus:outline-none focus:border-slate-300"
                                />
                            </div>
                        </div>
                    </div>
                    <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
                        {scoresPage.scores.length > 0 ? scoresPage.scores.map((entry) => (
                            <Link
                                key={entry.id}
                                to={`/genz-credit-scoring/admin/scores/${entry.id}`}
                                className="block group"
                            >
                                <div className="flex items-center justify-between p-3 rounded-lg border border-slate-200 bg-slate-50 hover:bg-white hover:border-slate-300 transition-all text-xs">
                                    <div className="min-w-0">
                                        <p className="text-slate-900 font-medium truncate flex items-center gap-1.5">
                                            {entry.user_name || entry.user_email || "Unknown user"}
                                            {entry.is_demo && <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">DEMO</span>}
                                        </p>
                                        <p className="text-slate-500 truncate">{entry.user_email || "—"}</p>
                                    </div>
                                    <div className="flex items-center gap-3 shrink-0 ml-3">
                                        <div className="text-right">
                                            <p className="font-mono font-bold" style={{ color: TIER_COLORS[entry.tier] || "#334155" }}>{entry.score || "—"}</p>
                                            <p className="text-slate-500">{formatDate(entry.created_at)}</p>
                                        </div>
                                        <ChevronRight size={14} className="text-slate-400 group-hover:text-slate-700" />
                                    </div>
                                </div>
                            </Link>
                        )) : <p className="text-slate-500 text-sm">No score records match search.</p>}
                    </div>
                    <div className="flex items-center justify-end mt-3">
                        <Pagination page={scoresPage.page} totalPages={scoresPage.total_pages} onPage={setScoresPageNum} />
                    </div>
                </section>

                <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
                    <h3 className="font-heading text-lg font-medium text-slate-900 mb-4">Consent Audit Trail</h3>
                    <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
                        {auditPage.entries.length > 0 ? auditPage.entries.map((entry) => (
                            <div key={entry.id} className="flex items-center justify-between p-2.5 rounded-lg border border-slate-200 bg-slate-50 text-xs">
                                <div className="min-w-0">
                                    <p className="text-slate-800 font-medium">{entry.action || "Action"}</p>
                                    <p className="text-slate-500">{entry.scope || "scope"}</p>
                                </div>
                                <span className="text-slate-500 shrink-0 ml-3">{formatDate(entry.timestamp)}</span>
                            </div>
                        )) : <p className="text-slate-500 text-sm">No consent changes recorded.</p>}
                    </div>
                    <div className="flex items-center justify-end mt-3">
                        <Pagination page={auditPage.page} totalPages={auditPage.total_pages} onPage={setAuditPageNum} />
                    </div>
                </section>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
                    <h3 className="font-heading text-lg font-medium text-slate-900 mb-4">Connector Status</h3>
                    <div className="space-y-2.5">
                        {connectors.map((connector) => {
                            const badge = statusBadge[connector.status];
                            return (
                                <div key={connector.name} className="flex items-center justify-between p-3 rounded-lg border border-slate-200 bg-slate-50">
                                    <div className="flex items-center gap-2">
                                        <connector.icon size={15} className="text-slate-500" />
                                        <span className="text-slate-800 text-sm">{connector.name}</span>
                                    </div>
                                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${badge.cls}`}>{badge.label}</span>
                                </div>
                            );
                        })}
                    </div>
                </section>

                <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
                    <div className="flex items-center gap-2 mb-4">
                        <Settings size={18} className="text-slate-500" />
                        <h3 className="font-heading text-lg font-medium text-slate-900">Feature Flags</h3>
                    </div>
                    <div className="space-y-2.5">
                        {flags.map((flag) => (
                            <div key={flag.name} className="flex items-center justify-between p-3 rounded-lg border border-slate-200 bg-slate-50">
                                <div className="min-w-0">
                                    <p className="text-slate-900 text-sm font-medium">{flag.name}</p>
                                    <p className="text-slate-500 text-xs">{flag.value}</p>
                                </div>
                                <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${flag.active ? "bg-emerald-500" : "bg-slate-300"}`} />
                            </div>
                        ))}
                    </div>
                </section>
            </div>
        </div>
    );
}
